import React from 'react';
import {
  Search,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Info,
  Music,
  ArrowLeft,
  Users,
  ArrowRight,
} from "lucide-react";
import FormCard from '../ui/FormCard';
import AvailableDancerCard from './AvailableDancerCard';
import SelectedDancerCard from './SelectedDancerCard';
import WarningModal from './WarningModal';
import RemoveDancerConfirmationModal from './RemoveDancerConfirmationModal';

import { DancersStepViewProps } from '../../types/dancers';
import { Participant } from '../../types/registration';

/**
 * View component for the dancers step
 */
const DancersStepView: React.FC<DancersStepViewProps> = ({
  loading,
  error,
  dancers,
  filteredDancers,
  searchQuery,
  setSearchQuery,
  filteredSelectedDancers,
  selectedSearchQuery,
  setSelectedSearchQuery,
  selectedDancers,
  showWarning,
  warningDancer,
  cancelDeselect,
  showRemoveConfirmation,
  dancerToRemove,
  confirmDancerRemoval,
  cancelDancerRemoval,
  workshopLevels,
  totalPrice,
  ageDivisions,
  isDarkMode,
  
  // Utility functions
  calculateAge,
  getInitials,
  formatAgeRange,
  getSuggestedWorkshopLevel,
  isDancerSelected,
  formatCurrency,
  findAgeGroupInfo,
  getWorkshopLevel,
  
  // Event handlers
  toggleDancerSelection,
  updateDancerOptions,
  onNext,
  onBack,
  renderNavigation
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={`p-4 rounded-lg ${
          isDarkMode
            ? "bg-red-500/10 border border-red-500/20 text-red-400"
            : "alert alert-error shadow-lg"
        }`}
      >
        <AlertTriangle className="h-6 w-6 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full">
      {/* Fixed Header */}
      <div
        className={`p-4 md:p-6 border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4">
          <div className="flex-shrink-0">
            <h2
              className={`responsive-heading font-bold mb-2 flex items-center ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              <Users className="h-6 w-6 mr-2 text-blue-500" />
              Select Dancers
            </h2>
            <p className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              Select dancers to register for this event
            </p>
          </div>

          {/* Information Alert */}
          <div
            className={`flex items-center p-2 px-3 rounded-lg border flex-shrink-0 md:max-w-xl lg:max-w-2xl xl:max-w-3xl ${
              isDarkMode
                ? "border-blue-800 text-blue-300 bg-blue-900/10"
                : "border-blue-200 text-blue-800 bg-blue-50"
            }`}
          >
            <Info className="h-5 w-5 mr-2 flex-shrink-0" />
            <p className="text-sm">
              To add new dancers, please visit the Studio page in your dashboard
              first.
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="p-4 md:p-6">
        <div className="space-y-8">
          {/* Global warning if no workshop levels */}
          {workshopLevels.length === 0 && (
            <div
              className={`p-4 rounded-lg border ${
                isDarkMode
                  ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-300"
                  : "bg-yellow-50 border-yellow-200 text-yellow-700"
              }`}
            >
              <div className="flex">
                <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
                <div>
                  <h3 className="font-medium">
                    Missing Workshop Configuration
                  </h3>
                  <p className="mt-1">
                    This event does not have any workshop levels configured.
                    Please contact the event organizer to set up the proper
                    registration options before proceeding.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Selected Count moved into Selected Dancers card subtitle */}

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Available Dancers */}
            <FormCard
              title="Available Dancers"
              subtitle={`${filteredDancers.length} dancers found`}
              icon={
                <Users
                  className={`h-5 w-5 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                />
              }
              className=""
              isDarkMode={isDarkMode}
            >
              {/* Search inside Available Dancers card */}
              <div
                className={`relative mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                <input
                  type="text"
                  className={`form-input pr-10 pl-4 py-2 w-full rounded-lg border ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      : "bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  } focus:ring-1 focus:outline-none transition-colors duration-200`}
                  placeholder="Search available dancers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <Search
                    className={`h-5 w-5 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  />
                </div>
              </div>
              <div className="overflow-y-auto max-h-[400px] custom-scrollbar">
                {filteredDancers.length === 0 ? (
                  <div className="text-center py-6">
                    <User
                      className={`h-12 w-12 mx-auto mb-2 ${
                        isDarkMode ? "text-gray-600" : "text-gray-400"
                      }`}
                    />
                    <p
                      className={`${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      No dancers found
                    </p>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      Try adjusting your search
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredDancers.map((dancer) => (
                      <AvailableDancerCard
                        key={dancer.dancerId}
                        dancer={dancer}
                        isSelected={isDancerSelected(dancer.dancerId || '')}
                        toggleDancerSelection={toggleDancerSelection}
                        calculateAge={calculateAge}
                        getInitials={getInitials}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </div>
                )}
              </div>
            </FormCard>

            {/* Selected Dancers */}
            <FormCard
              title="Selected Dancers"
              subtitle={`${filteredSelectedDancers.length} ${
                filteredSelectedDancers.length === 1 ? "dancer" : "dancers"
              } shown • ${selectedDancers.length} ${
                selectedDancers.length === 1 ? "dancer" : "dancers"
              } selected`}
              icon={
                <CheckCircle
                  className={`h-5 w-5 ${
                    isDarkMode ? "text-green-400" : "text-green-600"
                  }`}
                />
              }
              className=""
              isDarkMode={isDarkMode}
            >
              {/* Search inside Selected Dancers card */}
              <div
                className={`relative mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                <input
                  type="text"
                  className={`form-input pr-10 pl-4 py-2 w-full rounded-lg border ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      : "bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  } focus:ring-1 focus:outline-none transition-colors duration-200`}
                  placeholder="Search selected dancers..."
                  value={selectedSearchQuery}
                  onChange={(e) => setSelectedSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <Search
                    className={`h-5 w-5 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  />
                </div>
              </div>
              <div className="overflow-y-auto max-h-[400px] custom-scrollbar">
                {filteredSelectedDancers.length === 0 ? (
                  <div className="text-center py-6">
                    <User
                      className={`h-12 w-12 mx-auto mb-2 ${
                        isDarkMode ? "text-gray-600" : "text-gray-400"
                      }`}
                    />
                    <p
                      className={`${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      No selected dancers match your search
                    </p>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      Adjust your search or clear it to see all
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredSelectedDancers.map((dancer) => (
                      <SelectedDancerCard
                        key={dancer.dancerId || dancer.participantId || dancer.id}
                        dancer={dancer}
                        findAgeGroupInfo={findAgeGroupInfo}
                        workshopLevels={workshopLevels}
                        formatAgeRange={formatAgeRange}
                        calculateAge={calculateAge}
                        getInitials={getInitials}
                        getSuggestedWorkshopLevel={getSuggestedWorkshopLevel}
                        formatCurrency={formatCurrency}
                        getWorkshopLevel={getWorkshopLevel}
                        updateDancerOptions={updateDancerOptions}
                        toggleDancerSelection={toggleDancerSelection}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Total Price Section */}
              {selectedDancers.length > 0 && (
                <div
                  className={`mt-4 pt-3 border-t ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span
                      className={`font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Total Workshop Fees:
                    </span>
                    <span
                      className={`text-lg font-semibold px-3 py-1 rounded-lg ${
                        isDarkMode
                          ? "bg-green-900/30 text-green-400"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {formatCurrency(totalPrice)}
                    </span>
                  </div>
                  <p
                    className={`text-xs mt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Workshop fees are per dancer based on their selected level
                  </p>
                </div>
              )}
            </FormCard>
          </div>
          
          {/* Warning Modal for routine dancers */}
          {showWarning && warningDancer && (
            <WarningModal 
              isOpen={showWarning}
              dancer={warningDancer} 
              onClose={cancelDeselect} 
              isDarkMode={isDarkMode}
            />
          )}
          
          {/* Confirmation Modal for removing dancers */}
          {showRemoveConfirmation && dancerToRemove && (
            <RemoveDancerConfirmationModal
              isOpen={showRemoveConfirmation}
              dancer={dancerToRemove}
              onConfirm={confirmDancerRemoval}
              onCancel={cancelDancerRemoval}
              isDarkMode={isDarkMode}
            />
          )}
        </div>
      </div>

      {/* Render standardized navigation */}
      {renderNavigation && renderNavigation(
        onNext,
        onBack,
        selectedDancers.some(dancer => !getWorkshopLevel(dancer)) ? false : selectedDancers.length > 0,
        "Continue"
      )}
    </div>
  );
};

export default DancersStepView; 